/**
 * The mention grammar of a note. A note's stored text is plain text with mention tokens
 * `@[Vorname Nachname](user:<uuid>)`; the server derives the mention set from that text
 * (jupiter/services/notes.py, MENTION_RE), and this file is the client's only reader and
 * writer of the grammar: the renderer, the editor serialisation and the composer's
 * length check all go through it. No DOM, no React.
 *
 * Grammar (identical to the server's): `@[` label `](user:` uuid `)`, label 0 to 80
 * characters without `[`, `]` or a line break, uuid in canonical 8-4-4-4-12 hex form.
 */

import type { JSONContent } from "@tiptap/react";

const MENTION_SOURCE =
  "@\\[([^\\[\\]\\n]{0,80})\\]\\(user:" +
  "([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})\\)";

/**
 * Global, so it carries `lastIndex` state: always `matchAll` or `replace` with it, never
 * `exec`/`test` in a loop shared between callers.
 */
export const MENTION_TOKEN_RE = new RegExp(MENTION_SOURCE, "g");

export type NoteSegment =
  | { kind: "text"; text: string }
  | { kind: "mention"; userId: string; label: string };

/** The stored text split into plain text and mention segments, in order. */
export function parseNoteText(text: string): NoteSegment[] {
  const segments: NoteSegment[] = [];
  const pushText = (value: string) => {
    if (value === "") return;
    const last = segments[segments.length - 1];
    if (last?.kind === "text") {
      last.text += value;
    } else {
      segments.push({ kind: "text", text: value });
    }
  };

  let cursor = 0;
  for (const match of text.matchAll(MENTION_TOKEN_RE)) {
    pushText(text.slice(cursor, match.index));
    segments.push({
      kind: "mention",
      userId: match[2].toLowerCase(),
      label: match[1],
    });
    cursor = match.index + match[0].length;
  }
  pushText(text.slice(cursor));
  return segments;
}

/** What the reader sees: every token replaced by `@` and its label. */
export function flattenNoteText(text: string): string {
  return text.replace(MENTION_TOKEN_RE, (_token, label: string) => `@${label}`);
}

const MAX_LABEL_LENGTH = 80;

/** A label the grammar accepts: no brackets, parentheses or line breaks, trimmed, at most 80 characters. */
export function sanitizeMentionLabel(label: string): string {
  return label
    .replace(/[[\]()\r\n]+/g, "")
    .trim()
    .slice(0, MAX_LABEL_LENGTH)
    .trim();
}

/** The token that `parseNoteText` reads back (the label is sanitised). */
export function mentionToken(userId: string, label: string): string {
  return `@[${sanitizeMentionLabel(label)}](user:${userId})`;
}

/**
 * The editor document as the text that is stored and sent: paragraphs and hard breaks both
 * become a line break, a mention node becomes its token. The inverse of `noteTextToDoc`.
 */
export function noteDocToText(doc: JSONContent): string {
  const inline = (node: JSONContent): string => {
    switch (node.type) {
      case "text":
        return node.text ?? "";
      case "hardBreak":
        return "\n";
      case "mention":
        return mentionToken(
          String(node.attrs?.id ?? ""),
          String(node.attrs?.label ?? ""),
        );
      default:
        return (node.content ?? []).map(inline).join("");
    }
  };
  return (doc.content ?? []).map(inline).join("\n");
}

/** A stored text as editor content: one paragraph per line, tokens as mention nodes. */
export function noteTextToDoc(text: string): JSONContent {
  return {
    type: "doc",
    content: text.split("\n").map((line): JSONContent => {
      const content = parseNoteText(line).map(
        (segment): JSONContent =>
          segment.kind === "text"
            ? { type: "text", text: segment.text }
            : {
                type: "mention",
                attrs: { id: segment.userId, label: segment.label },
              },
      );
      return content.length === 0
        ? { type: "paragraph" }
        : { type: "paragraph", content };
    }),
  };
}
